import { useCallback, useState, useRef, useEffect } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { theme } from '../../styles/theme'
import { createUseStyles } from '../../helpers/createStyles'
import useSnapshot from '../../store/useSnapshot'
import { getSiteData } from '../../store/layoutSlice'
import InputContainer from '../TradeRegistration/InputContainer'
import Button from '../Button'
import RichContent from '../RichContent'
import CountrySelect from '../TradeRegistration/CountrySelect'
import ShowroomSelect from './ShowroomSelect'
import ResponsiveImage from '../ResponsiveImage'
import cn from 'classnames'
import gsap from 'gsap'
import use100vhStyle from '../../hooks/use100vh'
import Section from '../Section'

const SignUpForm = ({ page }) => {
  const styles = useStyles()
  const { signUpForm: { title, copyStart, copyEnd, successTitle, successCopy, ctaLabel, desktopImage, mobileImage, displayShowroomDropdown, showroomDropdownLabel, isShowroomDropdownRequired }, klaviyoListId } = page
  const contentRef = useRef()
  const formRef = useRef()
  const methods = useForm({
    mode: 'onTouched',
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      country: '',
      showroom: ''
    }
  })

  const snap = useSnapshot()
  const locale = getSiteData(snap).locale

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const imageRef = useRef()
  const height = use100vhStyle()

  const initAnimation = useCallback(async () => {
    const timeline = gsap.timeline()
    timeline.fromTo(contentRef.current, { y: '-100%' }, { y: 0, duration: 1.2, delay: 0.5, ease: 'expo.inOut' })
    timeline.fromTo(contentRef.current.children[0], { y: '100%' }, { y: 0, duration: 1.2, ease: 'expo.inOut' }, '-=1.2')
    timeline.fromTo(imageRef.current, { scale: 1.1 }, { scale: 1, duration: 1.2, ease: 'expo.inOut' }, '-=1.2')
    timeline.fromTo(formRef.current.children, { x: -24, opacity: 0 }, { opacity: 1, x: 0, duration: 0.8, stagger: 0.05, ease: 'expo.inOut' }, '-=1')
  }, [])

  useEffect(initAnimation, [initAnimation])
  const onImageLoaded = useCallback(initAnimation, [initAnimation])

  const onSubmit = useCallback(async (data) => {
    setLoading(true)
    const response = await window.fetch('/api/newsletter', {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        locale,
        klaviyoListId
      })
    })
    setLoading(false)
    if (response.ok) {
      setSuccess(true)
    } else {
      setError('Oops, something went wrong')
    }
  }, [locale, klaviyoListId])

  return (
    <Section className={styles.formWrapper} fullWidth ref={contentRef} style={{ height }}>
      <div className={cn(styles.grid, styles.contentInner)}>
        <ResponsiveImage
            image={desktopImage}
            className={styles.heroImage}
            mobileImage={mobileImage}
            loading='eager'
            fadeIn={false}
            ref={imageRef}
            onImageLoaded={onImageLoaded}
          />
        <div className={styles.backgroundOverlay}></div>
        <FormProvider {...methods}>
          {success && (
            <div className={styles.root}>
              <h2>{successTitle}</h2>
              <p className={styles.copyStart}>{successCopy}</p>
            </div>
          )}
          {!success && (
            <form onSubmit={methods.handleSubmit(onSubmit)} className={styles.root} ref={formRef}>
              <h2>{title}</h2>
              <RichContent content={copyStart} className={styles.copyStart} />
              <InputContainer lightTheme={true} name='first_name' required placeholder='First Name' />
              <InputContainer lightTheme={true} name='last_name' required placeholder='Last Name' />
              <InputContainer lightTheme={true} name='email' type='email' required placeholder='Email' />
              <InputContainer lightTheme={true} name='country' required placeholder='Country' InputComponent={CountrySelect} />
              {displayShowroomDropdown && (
                <InputContainer lightTheme={true} name='showroom' required={isShowroomDropdownRequired} placeholder={showroomDropdownLabel || 'Showroom'} InputComponent={ShowroomSelect} />
              )}
              {error && <div className={styles.error}>{error}</div>}
              <Button disabled={loading} type='submit'>{loading ? 'Sending...' : ctaLabel}</Button>
              <RichContent content={copyEnd} className={styles.copyEnd} />
            </form>
          )}
        </FormProvider>
      </div>
    </Section>
  )
}

const useStyles = createUseStyles({
  grid: {
    ...theme.gridStyle
  },
  formWrapper: {
    position: 'relative',
    transform: 'translate(0, 100%)',
    overflow: 'hidden',
    [theme.breakpoints.up('md')]: {
      marginBottom: `${theme.gutter.md}px`
    }
  },
  contentInner: {
    height: '100%',
    width: '100%',
    transform: 'translate(0, -100%)',
    marginBottom: theme.spacing(3),
    paddingLeft: `${theme.gutter.sm}px`,
    paddingRight: `${theme.gutter.sm}px`,
    [theme.breakpoints.up('md')]: {
      paddingLeft: `${theme.gutter.md}px`,
      paddingRight: `${theme.gutter.md}px`,
      marginBottom: theme.spacing(10)
    }
  },
  backgroundOverlay: {
    background: 'rgba(0, 0, 0, 0.65)',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: -1
  },
  heroImage: {
    position: 'absolute !important',
    height: '100%',
    width: '100%',
    zIndex: -1,
    '> *': {
      paddingTop: '0 !important'
    }
  },
  root: {
    gridColumn: '1 / span 8',
    display: 'flex',
    minHeight: '100vh',
    flexDirection: 'column',
    justifyContent: 'center',
    paddingTop: theme.spacing(20),
    textAlign: 'left',
    color: theme.colors.pageTheme.default.background,
    '& button': {
      color: theme.colors.pageTheme.default.background,
      borderColor: theme.colors.pageTheme.default.background
    },
    '.dialog-grid &': {
      paddingTop: theme.gutter.sm
    },
    [theme.breakpoints.up('md')]: {
      gridColumn: '5 / span 3'
    }
  },
  copyStart: {
    marginBottom: theme.spacing(5)
  },
  copyEnd: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(5)
  },
  error: {
    marginBottom: theme.spacing(2),
    color: theme.colors.error
  }
})

export default SignUpForm
